import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from '../common/Loader/Loader';
import ConfirmMessage from '../common/ConfirmMessage/ConfirmMessage';
import API from '../../utils/ApiConfig';
import { REVIEW_DATA_ONBOARDING, REVIEW_DATA_ONBOARDING_SEND_TO_SUPPORT } from '../../utils/ApiEndpoints';
import InputNumberPM from '../common/InputNumberPM/InputNumberPM';
import InputTextAreaPM from '../common/InputTextAreaPM/InputTextAreaPM';
import DataTablePM from '../common/DataTablePM/DataTablePM';
import { Column } from 'primereact/column';
import { NumberFormatter } from '../../utils/NumberFormatter';
import AutomatedOnboardingFooter from '../AutomatedOnboardingFooter/AutomatedOnboardingFooter';
import ProgressDialog from '../common/ProgressDialog/ProgressDialog';
import { REVIEW_DATA_CALCULATE_DESTINATION } from '../../utils/WebSocketDestinations';
import { useUnload } from '../../utils/CustomHooks';

const AutomatedOnboardingReviewData = props => {
  const {
    commonProps,
    versionName,
    activeStep,
    onActiveStepChange,
    onInitializationError,
    onDataSaveError,
    onNavigationError,
    onCustomError,
    onDataSaveNotification,
    onCustomNotification,
  } = props;
  const {
    services: { CommonService },
  } = commonProps;

  const [pageInitialized, setPageInitialized] = useState(false); //...flag indicates if the page was loaded properly (no error)
  const [isLoading, setLoading] = useState(false);

  const [confirmMessage, setConfirmMessage] = useState(null);
  const [confirmButtonText, setConfirmButtonText] = useState(null);
  const [showConfirmMessage, setShowConfirmMessage] = useState(false);
  const [onConfirmFunction, setOnConfirmFunction] = useState(null);
  const closeConfirmMessage = () => setShowConfirmMessage(false);

  const { t } = useTranslation();
  const tc = (e, data) => (data ? t(`autoonboard_review_data:${e}`, data) : t(`autoonboard_review_data:${e}`));
  const tcc = (e, data) => (data ? t(`common:${e}`, data) : t(`common:${e}`));

  const isAutoOnboardingWithoutSampleBudgetEnabled = CommonService.isAutoOnboardingWithoutSampleBudgetEnabled;

  const [showProgress, setShowProgress] = useState(false);
  const [destroyProgress, setDestroyProgress] = useState(false);
  useUnload(() => setDestroyProgress(true));

  const PROGRESS_BAR_MIN_TIME = 5; // seconds
  const STEP = {
    Loading: 0,
    Loaded: 1,
    Editing: 2,
    Sent: 3,
  };

  const [step, setStep] = useState(STEP.Loading);
  const [formValid, setFormValid] = useState(false);

  const [data, setData] = useState(null);
  const [comments, setComments] = useState(null);
  const commentsCharacterLimit = 250;

  // table
  const [tableData, setTableData] = useState([
    {
      label: tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_HEADER_ASSETS'),
      calculatedAmount: tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_VALUE_LOADING'),
      id: 'user-glAssets',
    },
    {
      label: tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_HEADER_LIABILITIES'),
      calculatedAmount: tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_VALUE_LOADING'),
      id: 'user-glLiabilities',
    },
    {
      label: tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_HEADER_EQUITY'),
      calculatedAmount: tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_VALUE_LOADING'),
      id: 'user-glEquity',
    },
    {
      label: tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_HEADER_NET_INCOME'),
      calculatedAmount: tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_VALUE_LOADING'),
      id: 'user-glNetIncome',
    },
    {
      label: tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_HEADER_ENDING_BALANCE'),
      calculatedAmount: tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_VALUE_LOADING'),
      id: 'user-glEndingBalance',
    },
  ]);

  // columns
  const [gridColumns, setGridColumns] = useState([
    {
      field: 'label',
    },
    {
      field: 'calculatedAmount',
      header: tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_HEADER_IMPORTED_AMOUNT'),
    },
  ]);
  const inputColumn = {
    field: 'glAmount',
    header: tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_HEADER_GL_AMOUNT'),
  };

  // init
  useEffect(() => {
    setShowProgress(true);
    let startTime = Date.now();
    let isGLEditMode = false;
    //...first recalculate data in database (by POST) then load the data (by GET)
    API.post(REVIEW_DATA_ONBOARDING, {})
      .then(() => {
        API.get(REVIEW_DATA_ONBOARDING, {})
          .then(response => {
            const { data } = response;
            isGLEditMode = setStepAndReturnIsGLEditMode(data);
            setData(data);
            setComments(data.commentsForSupport);
            updateTableData(data);
            updateFormValid();
            if (data.sentToSupport) {
              showGLAmountColumn();

              setTimeout(() => {
                focus('.pm-next-button');
              }, 0);
            } else {
              if (isGLEditMode) {
                showGLAmountColumn();
              }
            }
            setPageInitialized(true);
          })
          .catch(err => {
            onInitializationError(err);
          });
      })
      .catch(err => {
        onInitializationError(err);
      })
      .finally(() => {
        let gapTime = PROGRESS_BAR_MIN_TIME * 1000 - (Date.now() - startTime);
        if (gapTime > 0) {
          const timer = setTimeout(() => {
            clearTimeout(timer);
            setShowProgress(false);
            setDestroyProgress(true);
          }, gapTime);
        } else {
          setShowProgress(false);
          setDestroyProgress(true);
        }
      });
  }, []);

  useEffect(() => {
    if (!showProgress && data != null) {
      if (!data.sentToSupport) {
        if (checkIfGLFieldsHaveContent(data)) {
          setTimeout(() => {
            focus('#user-glAssets');
          }, 0);
        } else {
          setTimeout(() => {
            focus('.automated-onboarding-review-data-send-enter');
          }, 0);
        }
      }
    }
  }, [showProgress, data]);

  const focus = selector => {
    $(selector).focus();
  };

  const glAmountEditor = rowData => {
    if (step === STEP.Sent) {
      return rowData.glAmount;
    } else {
      return (
        <Fragment>
          <InputNumberPM
            value={rowData.inputAmount}
            placeholder={tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_VALUE_PLACEHOLDER')}
            mode="decimal"
            fractionDigits={2}
            maxIntegerDigits={13} // <10T
            className="automated-onboarding-review-data-table-input"
            onValueChange={e => setInputAmount(e, rowData)}
            inputId={rowData.id}
          />
          <span className="automated-onboarding-review-data-table-star">*</span>
        </Fragment>
      );
    }
  };

  const setInputAmount = (evt, rowData) => {
    rowData.inputAmount = evt.value;
    updateFormValid();
  };

  const updateFormValid = () => {
    let valid = true;
    tableData.forEach(function (item) {
      if (item.inputAmount === undefined || item.inputAmount === null || item.inputAmount === '') {
        valid = false;
      }
    });
    setFormValid(valid);
  };

  const columns = gridColumns.map(({ field, header, style, headerStyle }, index) => {
    return <Column key={index} field={field} header={header} style={style} headerStyle={headerStyle} body={field === 'glAmount' && glAmountEditor} />;
  });

  const updateTableData = data => {
    tableData[0].calculatedAmount = NumberFormatter(data.calculatedTotalAssets);
    tableData[0].inputAmount = data.glTotalAssets;

    tableData[1].calculatedAmount = NumberFormatter(data.calculatedTotalLiabilities);
    tableData[1].inputAmount = data.glTotalLiabilities;

    tableData[2].calculatedAmount = NumberFormatter(data.calculatedTotalEquity);
    tableData[2].inputAmount = data.glTotalEquity;

    tableData[3].calculatedAmount = NumberFormatter(data.calculatedNetIncome);
    tableData[3].inputAmount = data.glNetIncome;

    tableData[4].calculatedAmount = NumberFormatter(data.calculatedEndingCashBalance);
    tableData[4].inputAmount = data.glEndingCashBalance;

    if (data.sentToSupport) {
      updateGLAmount();
    }
  };

  const updateGLAmount = () => {
    tableData.forEach(function (rowData) {
      if (rowData.inputAmount !== undefined && rowData.inputAmount !== null) {
        rowData.glAmount = NumberFormatter(rowData.inputAmount);
      }
    });
  };

  const clearUserInput = () => {
    tableData[0].inputAmount = data.glTotalAssets;
    tableData[1].inputAmount = data.glTotalLiabilities;
    tableData[2].inputAmount = data.glTotalEquity;
    tableData[3].inputAmount = data.glNetIncome;
    tableData[4].inputAmount = data.glEndingCashBalance;

    setComments(data.commentsForSupport);
  };

  const updateData = () => {
    data.glTotalAssets = tableData[0].inputAmount;
    data.glTotalLiabilities = tableData[1].inputAmount;
    data.glTotalEquity = tableData[2].inputAmount;
    data.glNetIncome = tableData[3].inputAmount;
    data.glEndingCashBalance = tableData[4].inputAmount;

    data.commentsForSupport = comments;
  };

  const getRequestInfo = () => {
    return {
      glTotalAssets: tableData[0].inputAmount,
      glTotalLiabilities: tableData[1].inputAmount,
      glTotalEquity: tableData[2].inputAmount,
      glNetIncome: tableData[3].inputAmount,
      glEndingCashBalance: tableData[4].inputAmount,
      commentsForSupport: comments,
    };
  };

  const showGLAmountColumn = () => {
    gridColumns[1].header = tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_HEADER_CALCULATED_AMOUNT');
    gridColumns.push(inputColumn);
  };

  const hideGLAmountColumn = () => {
    gridColumns[1].header = tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_HEADER_IMPORTED_AMOUNT');
    gridColumns.pop();
  };

  const setStepAndReturnIsGLEditMode = data => {
    let isGLEditMode = false;
    if (data.sentToSupport) {
      setStep(STEP.Sent);
    } else {
      if (checkIfGLFieldsHaveContent(data)) {
        setStep(STEP.Editing);
        isGLEditMode = true;
      } else {
        setStep(STEP.Loaded);
      }
    }

    return isGLEditMode;
  };

  const checkIfGLFieldsHaveContent = data => {
    if (
      hasNotNullContent(data.glTotalAssets) ||
      hasNotNullContent(data.glTotalLiabilities) ||
      hasNotNullContent(data.glTotalEquity) ||
      hasNotNullContent(data.glNetIncome) ||
      hasNotNullContent(data.glEndingCashBalance) ||
      hasNonEmptyContent(data.commentsForSupport)
    ) {
      return true;
    } else {
      return false;
    }
  };

  const hasNotNullContent = numContent => {
    return numContent !== undefined && numContent !== null;
  };

  const hasNonEmptyContent = strContent => {
    return strContent !== undefined && strContent !== null && strContent !== '';
  };

  // buttons
  const onEnterGLAmounts = () => {
    setStep(STEP.Editing);
    showGLAmountColumn();

    setTimeout(() => {
      focus('#user-glAssets');
    }, 0);
  };

  const onCancel = () => {
    setStep(STEP.Loaded);
    clearUserInput();
    updateFormValid();
    hideGLAmountColumn();
  };

  const onSendToSupport = () => {
    setConfirmMessage(tc('AUTOMATED_ONBOARDING_REVIEWDATA_SEND_TO_SUPPORT_CONFIRM_MESSAGE'));
    setConfirmButtonText(tc('AUTOMATED_ONBOARDING_REVIEWDATA_SEND_TO_SUPPORT_CONFIRM_OK'));
    setOnConfirmFunction(() => onConfirmSentToSupport);
    setShowConfirmMessage(true);
  };

  const onConfirmSentToSupport = () => {
    if (!pageInitialized) {
      onCustomError(null); //...unexpected error, call of this method should not be possible
      return;
    }

    setLoading(true);
    API.post(REVIEW_DATA_ONBOARDING_SEND_TO_SUPPORT, getRequestInfo())
      .then(() => {
        setStep(STEP.Sent);
        updateGLAmount();
        onCustomNotification(tc('AUTOMATED_ONBOARDING_REVIEWDATA_SEND_TO_SUPPORT_SUCCEED'));

        setTimeout(() => {
          focus('.pm-next-button');
        }, 0);
      })
      .catch(err => {
        onCustomError(err, tc('AUTOMATED_ONBOARDING_REVIEWDATA_SEND_TO_SUPPORT_FAIL'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // footer
  const next = (saveType, callbackfn) => {
    if (step !== STEP.Sent) {
      let confirmMsg;
      if (isAutoOnboardingWithoutSampleBudgetEnabled) {
        confirmMsg = tc('AUTOMATED_ONBOARDING_REVIEWDATA_DATA_VALID_CONFIRM_NO_SAMPLE_BUDGET_MESSAGE');
      } else {
        confirmMsg = tc('AUTOMATED_ONBOARDING_REVIEWDATA_DATA_VALID_CONFIRM_MESSAGE');
      }
      setConfirmMessage(confirmMsg);
      setConfirmButtonText(tc('AUTOMATED_ONBOARDING_REVIEWDATA_CONFIRM_OK'));
      setOnConfirmFunction(() => () => save(saveType, callbackfn));
      setShowConfirmMessage(true);
    } else {
      save(saveType, callbackfn);
    }
  };

  const save = (saveType, callback) => {
    if (!pageInitialized) {
      onDataSaveError(null); //...unexpected error, call of this method should not be possible
      return;
    }

    setLoading(true);
    API.put(REVIEW_DATA_ONBOARDING, {
      saveType: saveType,
      reviewDataDTO: getRequestInfo(),
    })
      .then(() => {
        updateData();
        callback();
      })
      .catch(err => {
        onDataSaveError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const footerBtnInfo = {
    backBtn: { disabled: false, showBtn: step !== STEP.Sent, click: save },
    nextBtn: { disabled: step === STEP.Editing, showBtn: true, click: next },
    saveBtn: { disabled: false, showBtn: true, click: save },
    saveExitBtn: { disabled: false, showBtn: true, click: save },
  };

  return (
    <div className="automated-onboarding-review-data">
      {isLoading && <Loader />}
      <ProgressDialog show={showProgress} destroy={destroyProgress} destination={REVIEW_DATA_CALCULATE_DESTINATION} />

      <ConfirmMessage
        open={showConfirmMessage}
        onConfirm={onConfirmFunction}
        handleClose={closeConfirmMessage}
        message={confirmMessage}
        confirmText={confirmButtonText}
      />

      <div className="automated-onboarding-review-data-content">
        <div className="automated-onboarding-title title">{tc('AUTOMATED_ONBOARDING_REVIEWDATA_TITLE')}</div>
        <div className="automated-onboarding-subtitle">
          {isAutoOnboardingWithoutSampleBudgetEnabled
            ? tc('AUTOMATED_ONBOARDING_REVIEWDATA_SUBTITLE_NO_SAMPLE_BUDGET')
            : tc('AUTOMATED_ONBOARDING_REVIEWDATA_SUBTITLE', { versionName })}
        </div>
        <div className="automated-onboarding-review-data-left">
          <div className="automated-onboarding-title title text-ellipsis" title={tc('AUTOMATED_ONBOARDING_REVIEWDATA_LEFT_TITLE')}>
            {tc('AUTOMATED_ONBOARDING_REVIEWDATA_LEFT_TITLE')}
          </div>

          <div className="automated-onboarding-review-data-date-info">
            <div className="automated-onboarding-review-data-data-row">
              <div className="automated-onboarding-review-data-date-header">{tc('AUTOMATED_ONBOARDING_REVIEWDATA_DATA_INCLUDED')}</div>
              {data && (
                <div className="automated-onboarding-review-data-date-value">
                  {data.startDataYear === data.endDataYear
                    ? data.startDataYear
                    : tc('AUTOMATED_ONBOARDING_REVIEWDATA_DATA_INCLUDED_THROUGH', {
                        startYear: data.startDataYear,
                        endYear: data.endDataYear,
                      })}
                </div>
              )}
            </div>
            <div className="automated-onboarding-review-data-data-row">
              <div className="automated-onboarding-review-data-date-header">{tc('AUTOMATED_ONBOARDING_REVIEWDATA_FISCAL_YEAR_BEGINS')}</div>
              <div className="automated-onboarding-review-data-date-value">{data && data.firstMonthInFiscalYear}</div>
            </div>
          </div>

          {step !== STEP.Loading && (
            <Fragment>
              <div className="automated-onboarding-title title">
                {step === STEP.Loaded
                  ? tc('AUTOMATED_ONBOARDING_REVIEWDATA_BOTTOM_TITLE_LOADED')
                  : step === STEP.Editing
                    ? tc('AUTOMATED_ONBOARDING_REVIEWDATA_BOTTOM_TITLE_EDITING')
                    : tc('AUTOMATED_ONBOARDING_REVIEWDATA_BOTTOM_TITLE_SENT')}
              </div>
              <div className="automated-onboarding-review-data-notification">
                {
                  <div className="automated-onboarding-review-data-notification-row">
                    <span className="material-icons automated-onboarding-review-data-notification-yes">check_circle</span>
                    <div className="automated-onboarding-review-data-notification-value">
                      {step === STEP.Loaded && (
                        <span className="automated-onboarding-review-data-notification-yes">
                          {tc('AUTOMATED_ONBOARDING_REVIEWDATA_BOTTOM_NOTIFICATION_YES')}
                        </span>
                      )}
                      <span>
                        {step === STEP.Loaded
                          ? tc('AUTOMATED_ONBOARDING_REVIEWDATA_BOTTOM_NOTIFICATION_LOADED_YES')
                          : step === STEP.Editing
                            ? tc('AUTOMATED_ONBOARDING_REVIEWDATA_BOTTOM_NOTIFICATION_EDITING')
                            : tc('AUTOMATED_ONBOARDING_REVIEWDATA_BOTTOM_NOTIFICATION_SENT')}
                      </span>
                    </div>
                  </div>
                }
                {step === STEP.Loaded && (
                  <div className="automated-onboarding-review-data-notification-row">
                    <span className="material-icons automated-onboarding-review-data-notification-no">error</span>
                    <div className="automated-onboarding-review-data-notification-value">
                      <span className="automated-onboarding-review-data-notification-no">
                        {tc('AUTOMATED_ONBOARDING_REVIEWDATA_BOTTOM_NOTIFICATION_NO')}
                      </span>
                      <span>{tc('AUTOMATED_ONBOARDING_REVIEWDATA_BOTTOM_NOTIFICATION_LOADED_NO')}</span>
                    </div>
                  </div>
                )}
              </div>
            </Fragment>
          )}
        </div>

        <div className="automated-onboarding-review-data-right">
          <div className="automated-onboarding-title title text-ellipsis" title={tc('AUTOMATED_ONBOARDING_REVIEWDATA_RIGHT_TITLE')}>
            {tc('AUTOMATED_ONBOARDING_REVIEWDATA_RIGHT_TITLE')}
          </div>

          <div className="automated-onboarding-review-data-table-div">
            <div className="automated-onboarding-review-data-table">
              <DataTablePM
                showGridlines={false}
                value={tableData}
                columns={columns}
                header={
                  <Fragment>
                    <span>{tc('AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_HEADER')}</span>
                    {data && (
                      <span className="automated-onboarding-review-data-table-header-value">
                        {tc(
                          data.startPeriodForGeneratedYear === data.endPeriodForGeneratedYear
                            ? 'AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_HEADER_VALUE_ONE_PERIOD'
                            : 'AUTOMATED_ONBOARDING_REVIEWDATA_TABLE_HEADER_VALUE',
                          {
                            generatedYear: data.dataGeneratedYear,
                            startPeriod: data.startPeriodForGeneratedYear,
                            endPeriod: data.endPeriodForGeneratedYear,
                          }
                        )}
                      </span>
                    )}
                  </Fragment>
                }
              />
            </div>
          </div>

          <div className="automated-onboarding-review-data-send-div">
            {step === STEP.Editing ? (
              <div className="automated-onboarding-review-data-send-editing">
                <InputTextAreaPM
                  value={comments}
                  placeholder={tc('AUTOMATED_ONBOARDING_REVIEWDATA_COMMENTS_PLACEHOLDER')}
                  onChange={setComments}
                  maxLength={commentsCharacterLimit}
                  isFooter
                />
                <div className="automated-onboarding-review-data-send-editing-buttons">
                  <button
                    className="btn btn-sm btn-primary btn-none-navigational"
                    type="button"
                    disabled={!formValid || !pageInitialized}
                    onClick={onSendToSupport}>
                    {tc('AUTOMATED_ONBOARDING_REVIEWDATA_BUTTON_SEND_TO_SUPPORT')}
                  </button>
                  <button className="btn btn-sm btn-default" type="button" onClick={onCancel}>
                    {tcc('BUTTON_CANCEL')}
                  </button>
                </div>
              </div>
            ) : step === STEP.Sent ? (
              <div className="automated-onboarding-review-data-send-sent">
                <span className="automated-onboarding-review-data-send-sent-additional-comments">
                  <span className="automated-onboarding-review-data-send-sent-additional-comments-header">
                    {tc('AUTOMATED_ONBOARDING_REVIEWDATA_COMMENTS_HEADER')}
                  </span>
                  <span title={comments}>{comments}</span>
                </span>
              </div>
            ) : (
              <div className="automated-onboarding-review-data-send-load">
                <button
                  className="btn btn-sm btn-primary btn-none-navigational automated-onboarding-review-data-send-enter"
                  type="button"
                  disabled={!pageInitialized}
                  onClick={onEnterGLAmounts}
                  autoFocus>
                  {tc('AUTOMATED_ONBOARDING_REVIEWDATA_BUTTON_ENTER_GL_AMOUNTS')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <AutomatedOnboardingFooter
        commonProps={commonProps}
        btnInfo={footerBtnInfo}
        activeStep={activeStep}
        pageInitialized={pageInitialized}
        onActiveStepChange={s => onActiveStepChange(s)}
        onNavigationError={e => onNavigationError(e)}
        onDataSaveNotification={e => onDataSaveNotification(e)}
      />
    </div>
  );
};

export default AutomatedOnboardingReviewData;